import { RBTree } from 'bintrees';

const tmpRange = [0, 1];

/**
 * Table to manage ids that can be  reserved and released and keep the
 * id values as compact as possible.
 */
export class ReusableIds {
  /**
   * Construct the id table
   */
  constructor(lowId, highId, reserve) {
    this._lowId = lowId === undefined ? 0 : lowId | 0;
    this._highId = highId === undefined ? 4 * 1024 * 1024 * 1024 - 1 : highId | 0;
    this._availableIds = new RBTree(function (a, b) {return a[0] - b[0];});
    this._availableIds.insert([this._lowId + (reserve | 0), this._highId]);
  }

  /**
   * Get next id
   * @return {number|undefined} The next id or undefined if there aren't any more ids.
   */
  reserveId() {
    const range = this._availableIds.min();
    if (range === null)
    return undefined;
    const id = range[0];
    if (id >= range[1]) {
      // Remove the empty range.
      this._availableIds.remove(range);
    } else {
      // Updating the key of the RBTree without removing and inserting it
      // is probably not correct, but the ranges are all disjoint and so it should be OK
      ++range[0];
    }
    return id;
  }

  /**
   * Make an id available again
   * @param {number} id The id
   * @return {Boolean} True if the id can be released.
   */
  releaseId(id) {
    if (id < this._lowId || id > this._highId)
    return false;

    tmpRange[0] = id; // Only the first
    let ranges = this._availableIds.lowerBound(tmpRange);
    const upper = ranges.data();
    if (upper !== null) {
      // Make sure the id isn't already in a range
      if (upper[0] <= id && upper[1] >= id) {
        return false;
      }

      ranges.prev();
      const lower = ranges.data();
      if (id + 1 === upper[0]) {
        if (lower === null || id - 1 > lower[1]) {
          // Extend the upper range to include id
          // Updating the key of the RBTree without removing and inserting it
          // is probably not correct, but the ranges are all disjoint and so it should be OK
          upper[0] = id;
        } else {
          // Combine upper and lower ranges
          this._availableIds.remove(upper);
          this._availableIds.remove(lower);
          upper[0] = lower[0];
          this._availableIds.insert(upper);
        }
      } else if (lower === null || id - 1 > lower[1]) {
        // Add a new single id range
        this._availableIds.insert([id, id]);
      } else {
        // Extend the lower range
        // Updating the key of the RBTree without removing and inserting it
        // is probably not correct, but the ranges are all disjoint and so it should be OK
        lower[1] = id;
      }
    }

    return true;
  }

  isIdReserved(id) {
    if (id < this._lowId || id > this._highId)
    return false;

    tmpRange[0] = id; // Only the first
    const upper = this._availableIds.upperBound(tmpRange).prev();
    return !upper || id < upper[0] || id > upper[1];
  }
}